import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Loader2,
  Mail,
  Send,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Plus,
  X,
  Download,
  Search,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Switch } from "@/shared/ui/switch";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Badge } from "@/shared/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Progress } from "@/shared/ui/progress";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { toast } from "@/shared/widgets/toast";

type Settings = {
  enabled: boolean;
  audience_id: string | null;
  default_tags: string[];
  has_edge_function_url: boolean;
  has_webhook_secret: boolean;
  connected: boolean;
};

export default function MailchimpIntegrationPage() {
  const { t } = useTranslation("mailchimp");
  const [settings, setSettings] = useState<Settings | null>(null);
  const [audienceInput, setAudienceInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingToggle, setSavingToggle] = useState(false);
  const [savingAudience, setSavingAudience] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const [testing, setTesting] = useState(false);
  const [setupRunning, setSetupRunning] = useState(false);

  // Sync modal
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncStarting, setSyncStarting] = useState(false);
  const [syncBatchId, setSyncBatchId] = useState<string | null>(null);
  const [syncTotal, setSyncTotal] = useState(0);
  const [syncStatus, setSyncStatus] = useState<{
    status?: string;
    total_operations?: number;
    finished_operations?: number;
    errored_operations?: number;
  } | null>(null);

  // Members list
  type Member = {
    email_address: string;
    full_name?: string;
    status?: string;
    tags?: { id: number; name: string }[];
    timestamp_signup?: string;
    timestamp_opt?: string;
  };
  const PAGE_SIZE = 25;
  const [members, setMembers] = useState<Member[]>([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersOffset, setMembersOffset] = useState(0);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersSearch, setMembersSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  // Segmentation
  type SegRun = {
    id: string;
    created_at: string;
    version_tag: string;
    applied: boolean;
    applied_at: string | null;
    batch_id: string | null;
    summary: any;
  };
  const [segRuns, setSegRuns] = useState<SegRun[]>([]);
  const [segDiagnosing, setSegDiagnosing] = useState(false);
  const [segApplyingId, setSegApplyingId] = useState<string | null>(null);
  const [segLastSummary, setSegLastSummary] = useState<any>(null);
  const [segError, setSegError] = useState<string | null>(null);

  const loadSegRuns = async () => {
    const { data, error } = await supabase.functions.invoke("mailchimp-subscribe", {
      body: { action: "list_segmentation_runs" },
    });
    if (!error && data?.ok) setSegRuns(data.runs ?? []);
  };

  const runDiagnose = async () => {
    setSegDiagnosing(true);
    setSegError(null);
    try {
      const { data, error } = await supabase.functions.invoke("mailchimp-subscribe", {
        body: { action: "diagnose_segmentation" },
      });
      if (error || !data?.ok) throw new Error(data?.error ?? error?.message ?? "Erro desconhecido");
      setSegLastSummary(data.summary);
      toast.success("Diagnóstico concluído");
      await loadSegRuns();
    } catch (e: any) {
      const message = e.message === "forbidden"
        ? "A Edge Function recebeu a chamada, mas recusou a permissão de admin. Faça login novamente e tente de novo."
        : e.message;
      setSegError(message);
      toast.error("Falha no diagnóstico", message);
    } finally {
      setSegDiagnosing(false);
    }
  };

  const applyRun = async (runId: string) => {
    if (!confirm("Aplicar tags no Mailchimp para todos os membros deste diagnóstico?")) return;
    setSegApplyingId(runId);
    try {
      const { data, error } = await supabase.functions.invoke("mailchimp-subscribe", {
        body: { action: "apply_segmentation", run_id: runId },
      });
      if (error || !data?.ok) throw new Error(data?.error ?? error?.message ?? "erro");
      toast.success(`Aplicado: ${data.total} operações`, data.batch_id ?? undefined);
      await loadSegRuns();
    } catch (e: any) {
      toast.error("Falha ao aplicar", e.message);
    } finally {
      setSegApplyingId(null);
    }
  };

  const revertRun = async (runId: string) => {
    if (!confirm("Reverter (desativar tag de versão deste run)?")) return;
    try {
      const { data, error } = await supabase.functions.invoke("mailchimp-subscribe", {
        body: { action: "revert_segmentation", run_id: runId },
      });
      if (error || !data?.ok) throw new Error(data?.error ?? error?.message ?? "erro");
      toast.success("Reversão enviada");
      await loadSegRuns();
    } catch (e: any) {
      toast.error("Falha ao reverter", e.message);
    }
  };


  const loadMembers = async (offset = 0, search = "") => {
    setMembersLoading(true);
    const { data, error } = await supabase.functions.invoke("mailchimp-subscribe", {
      body: { action: "list_members", offset, count: PAGE_SIZE, search: search || undefined },
    });
    setMembersLoading(false);
    if (error || (data as any)?.error) {
      toast.error((data as any)?.error ?? error?.message ?? t("membersLoadError"));
      return;
    }
    setMembers(((data as any).members ?? []) as Member[]);
    setMembersTotal((data as any).total ?? 0);
    setMembersOffset(offset);
  };

  const exportMembers = async () => {
    setExporting(true);
    const { data, error } = await supabase.functions.invoke("mailchimp-subscribe", {
      body: { action: "export_members" },
    });
    setExporting(false);
    if (error || (data as any)?.error) {
      toast.error((data as any)?.error ?? error?.message ?? t("exportError"));
      return;
    }
    const list = ((data as any).members ?? []) as Member[];
    const escape = (v: unknown) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["email", "name", "status", "tags", "signup_at", "opt_in_at"];
    const rows = list.map((m) =>
      [
        m.email_address,
        m.full_name,
        m.status,
        (m.tags ?? []).map((t) => t.name).join("|"),
        m.timestamp_signup,
        m.timestamp_opt,
      ]
        .map(escape)
        .join(","),
    );
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mailchimp-members-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(t("exportSuccess", { count: list.length }));
  };

  const refreshSettings = async () => {
    const { data, error } = await supabase.functions.invoke("mailchimp-subscribe", {
      body: { action: "status" },
    });
    if (error || (data as any)?.error) {
      toast.error(t("loadError"));
      return null;
    }
    const s = (data as any)?.settings as Settings | undefined;
    if (s) {
      setSettings(s);
      setAudienceInput(s.audience_id ?? "");
      return s;
    }
    return null;
  };

  const runSetup = async (silent = false) => {
    setSetupRunning(true);
    const { error, data } = await supabase.functions.invoke("mailchimp-subscribe", {
      body: { action: "setup" },
    });
    setSetupRunning(false);
    if (error || (data as any)?.error) {
      if (!silent) toast.error(t("setupError"));
      return;
    }
    if (!silent) toast.success(t("setupSuccess"));
    await refreshSettings();
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const s = await refreshSettings();
      if (s && (!s.has_edge_function_url || !s.has_webhook_secret)) {
        await runSetup(true);
      }
      if (s?.connected) {
        loadMembers(0, "");
        loadSegRuns();
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSettings = async (patch: Record<string, unknown>) => {
    const { error, data } = await supabase.functions.invoke("mailchimp-subscribe", {
      body: { action: "update_settings", ...patch },
    });
    if (error || (data as any)?.error) return false;
    return true;
  };

  const toggleEnabled = async (value: boolean) => {
    if (!settings) return;
    const previous = settings.enabled;
    setSettings({ ...settings, enabled: value });
    setSavingToggle(true);
    const ok = await updateSettings({ enabled: value });
    setSavingToggle(false);
    if (!ok) {
      setSettings({ ...settings, enabled: previous });
      toast.error(t("saveError"));
    }
  };

  const saveAudience = async () => {
    if (!settings) return;
    setSavingAudience(true);
    const ok = await updateSettings({ audience_id: audienceInput.trim() || null });
    setSavingAudience(false);
    if (!ok) {
      toast.error(t("saveError"));
    } else {
      toast.success(t("saveSuccess"));
      await refreshSettings();
    }
  };

  const saveTags = async (next: string[]) => {
    if (!settings) return;
    const previous = settings.default_tags;
    setSettings({ ...settings, default_tags: next });
    setSavingTags(true);
    const ok = await updateSettings({ default_tags: next });
    setSavingTags(false);
    if (!ok) {
      setSettings({ ...settings, default_tags: previous });
      toast.error(t("saveError"));
    }
  };

  const addTag = () => {
    const v = tagInput.trim().toLowerCase();
    if (!v || !settings) return;
    if (settings.default_tags.includes(v)) {
      setTagInput("");
      return;
    }
    saveTags([...settings.default_tags, v]);
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    if (!settings) return;
    saveTags(settings.default_tags.filter((t) => t !== tag));
  };

  const sendTest = async () => {
    setTesting(true);
    const { error, data } = await supabase.functions.invoke("mailchimp-subscribe", {
      body: { action: "test" },
    });
    setTesting(false);
    if (error || (data as any)?.error) {
      toast.error(`${t("testError")}: ${(data as any)?.error ?? error?.message ?? ""}`);
    } else {
      toast.success(t("testSuccess"));
    }
  };

  const startSync = async () => {
    setSyncOpen(true);
    setSyncBatchId(null);
    setSyncStatus(null);
    setSyncStarting(true);
    const { error, data } = await supabase.functions.invoke("mailchimp-subscribe", {
      body: { action: "sync_existing" },
    });
    setSyncStarting(false);
    if (error || (data as any)?.error) {
      toast.error(`${t("syncError")}: ${(data as any)?.error ?? error?.message ?? ""}`);
      setSyncOpen(false);
      return;
    }
    setSyncTotal((data as any).total ?? 0);
    setSyncBatchId((data as any).batch_id ?? null);
  };

  // Poll sync status
  useEffect(() => {
    if (!syncBatchId || !syncOpen) return;
    let cancelled = false;
    const poll = async () => {
      const { data } = await supabase.functions.invoke("mailchimp-subscribe", {
        body: { action: "sync_status", batch_id: syncBatchId },
      });
      if (cancelled) return;
      if (data && !(data as any).error) {
        setSyncStatus(data as any);
        if ((data as any).status === "finished") return;
      }
      setTimeout(poll, 3000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [syncBatchId, syncOpen]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isConnected = !!settings?.connected;

  const progress =
    syncStatus?.total_operations && syncStatus.total_operations > 0
      ? Math.round(
          ((syncStatus.finished_operations ?? 0) / syncStatus.total_operations) * 100,
        )
      : 0;

  return (
    <div className="container mx-auto max-w-5xl space-y-6 py-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Mail className="h-6 w-6 text-yellow-500" />
            {t("title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("subtitle")}</p>
        </div>
        {isConnected ? (
          <Badge variant="secondary" className="gap-1 bg-emerald-500/10 text-emerald-600">
            <CheckCircle2 className="h-3.5 w-3.5" /> {t("connected")}
          </Badge>
        ) : (
          <Badge variant="secondary" className="gap-1 bg-amber-500/10 text-amber-600">
            <AlertCircle className="h-3.5 w-3.5" /> {t("notConfigured")}
          </Badge>
        )}
      </div>

      <Tabs defaultValue="subscribers" className="space-y-4">
        <TabsList>
          <TabsTrigger value="subscribers">Subscribers</TabsTrigger>
          <TabsTrigger value="segmentation">Segmentação</TabsTrigger>
          <TabsTrigger value="settings">Configuração</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("statusTitle")}</CardTitle>
          <CardDescription>{t("statusDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <div className="text-sm font-medium">{t("enabled")}</div>
              <p className="text-xs text-muted-foreground">{t("enabledDesc")}</p>
            </div>
            <div className="flex items-center gap-2">
              {savingToggle && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              )}
              <Switch
                checked={!!settings?.enabled}
                onCheckedChange={toggleEnabled}
                disabled={savingToggle}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="audience-id">{t("audienceId")}</Label>
            <div className="flex gap-2">
              <Input
                id="audience-id"
                value={audienceInput}
                onChange={(e) => setAudienceInput(e.target.value)}
                placeholder="a1b2c3d4e5"
              />
              <Button
                onClick={saveAudience}
                disabled={savingAudience || audienceInput === (settings?.audience_id ?? "")}
              >
                {savingAudience && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("save")}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{t("audienceIdDesc")}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button onClick={sendTest} disabled={testing || !isConnected}>
              {testing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {t("sendTest")}
            </Button>
            <Button variant="outline" onClick={() => runSetup(false)} disabled={setupRunning}>
              {setupRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("reconfigure")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("tagsTitle")}</CardTitle>
          <CardDescription>{t("tagsDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {settings?.default_tags?.length ? (
              settings.default_tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="gap-1.5 py-1.5">
                  {tag}
                  <button
                    onClick={() => removeTag(tag)}
                    className="rounded-full p-0.5 hover:bg-muted-foreground/20"
                    aria-label={t("removeTag")}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">{t("noTags")}</p>
            )}
            {savingTags && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>
          <div className="flex gap-2">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag();
                }
              }}
              placeholder={t("addTagPlaceholder")}
            />
            <Button variant="outline" onClick={addTag} disabled={!tagInput.trim()}>
              <Plus className="mr-1 h-4 w-4" /> {t("add")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t("autoTagsHint")}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("syncTitle")}</CardTitle>
          <CardDescription>{t("syncDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={startSync} disabled={!isConnected}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t("syncButton")}
          </Button>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="segmentation" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Segmentação inteligente</CardTitle>
          <CardDescription>
            Cruza a base do Mailchimp com dados do app (cadastro, assinatura, uso de preset, engajamento) e propõe tags em duas fases: diagnóstico (não altera nada) e aplicação em lote.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button onClick={runDiagnose} disabled={!isConnected || segDiagnosing}>
              {segDiagnosing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              Rodar diagnóstico
            </Button>
            <Button variant="outline" onClick={loadSegRuns} disabled={!isConnected}>
              <RefreshCw className="mr-2 h-4 w-4" /> Atualizar histórico
            </Button>
          </div>

          {segError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{segError}</AlertDescription>
            </Alert>
          )}

          {segLastSummary && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-2">
              <div className="font-medium">Último diagnóstico</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
                <div>Mailchimp: <strong>{segLastSummary.total_mailchimp_members}</strong></div>
                <div>Profiles c/ e-mail: <strong>{segLastSummary.total_profiles_with_email}</strong></div>
                <div>Match: <strong>{segLastSummary.matched}</strong></div>
                <div>MC sem profile: <strong>{segLastSummary.inconsistencies?.mc_without_profile ?? 0}</strong></div>
                <div>Profile sem MC: <strong>{segLastSummary.inconsistencies?.profile_without_mc ?? 0}</strong></div>
                <div>Duplicados: <strong>{segLastSummary.inconsistencies?.duplicates ?? 0}</strong></div>
              </div>
              <div className="flex flex-wrap gap-1 pt-2">
                {Object.entries(segLastSummary.tag_counts ?? {})
                  .sort((a: any, b: any) => b[1] - a[1])
                  .map(([tag, count]: any) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}: {count}
                    </Badge>
                  ))}
              </div>
            </div>
          )}

          {segRuns.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">Histórico</div>
              <div className="divide-y rounded-md border">
                {segRuns.map((r) => (
                  <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
                    <div className="space-y-0.5">
                      <div className="font-mono text-xs text-muted-foreground">{r.version_tag}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()} ·{" "}
                        {r.applied ? (
                          <span className="text-green-600">aplicado{r.batch_id ? ` (${r.batch_id})` : ""}</span>
                        ) : (
                          <span>pendente</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!r.applied && (
                        <Button
                          size="sm"
                          onClick={() => applyRun(r.id)}
                          disabled={segApplyingId === r.id}
                        >
                          {segApplyingId === r.id ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                          )}
                          Aplicar
                        </Button>
                      )}
                      {r.applied && (
                        <Button size="sm" variant="outline" onClick={() => revertRun(r.id)}>
                          Reverter
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="subscribers" className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>{t("membersTitle")}</CardTitle>
              <CardDescription>
                {t("membersDesc", { count: membersTotal })}
              </CardDescription>
            </div>
            <Button onClick={exportMembers} disabled={!isConnected || exporting} variant="outline">
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {t("exportCsv")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={membersSearch}
                onChange={(e) => setMembersSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    loadMembers(0, membersSearch);
                  }
                }}
                placeholder={t("membersSearchPlaceholder")}
                className="pl-9"
                disabled={!isConnected}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => loadMembers(0, membersSearch)}
              disabled={!isConnected || membersLoading}
            >
              {membersLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {t("refresh")}
            </Button>
          </div>

          <div className="overflow-hidden rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t("colEmail")}</th>
                  <th className="px-3 py-2 font-medium">{t("colName")}</th>
                  <th className="px-3 py-2 font-medium">{t("colStatus")}</th>
                  <th className="px-3 py-2 font-medium">{t("colTags")}</th>
                </tr>
              </thead>
              <tbody>
                {membersLoading && members.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    </td>
                  </tr>
                ) : members.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                      {t("noMembers")}
                    </td>
                  </tr>
                ) : (
                  members.map((m) => (
                    <tr key={m.email_address} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{m.email_address}</td>
                      <td className="px-3 py-2">{m.full_name || "—"}</td>
                      <td className="px-3 py-2">
                        <Badge variant="secondary" className="capitalize">
                          {m.status ?? "—"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(m.tags ?? []).map((tg) => (
                            <Badge key={tg.id} variant="outline" className="text-xs">
                              {tg.name}
                            </Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!membersSearch && membersTotal > PAGE_SIZE && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {membersOffset + 1}–{Math.min(membersOffset + members.length, membersTotal)} /{" "}
                {membersTotal}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={membersOffset === 0 || membersLoading}
                  onClick={() => loadMembers(Math.max(0, membersOffset - PAGE_SIZE), "")}
                >
                  {t("prev")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={membersOffset + PAGE_SIZE >= membersTotal || membersLoading}
                  onClick={() => loadMembers(membersOffset + PAGE_SIZE, "")}
                >
                  {t("next")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("syncDialogTitle")}</DialogTitle>
            <DialogDescription>{t("syncDialogDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {syncStarting && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> {t("syncPreparing")}
              </div>
            )}
            {!syncStarting && syncTotal > 0 && (
              <div className="text-sm">
                {t("syncQueued", { count: syncTotal })}
              </div>
            )}
            {syncStatus && (
              <>
                <Progress value={progress} />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {syncStatus.finished_operations ?? 0} /{" "}
                    {syncStatus.total_operations ?? 0}
                  </span>
                  <span>
                    {syncStatus.status}
                    {syncStatus.errored_operations
                      ? ` · ${syncStatus.errored_operations} ${t("errors")}`
                      : ""}
                  </span>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSyncOpen(false)}>
              {t("close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
